// Display resolver for the generation-completion Notifications feed (REF-6306). Pure functions,
// no React — mirrors the presentation-glue role whatsNew.data.ts plays for What's New (icon-name →
// Lucide mapping, no JSX). Consumed by the notification bell UI (owned by another agent).
//
// Catalog: src/i18n/locales/{locale}/notifications.json, namespace "notifications" (registered in
// src/i18n/config.ts + src/i18n/index.ts). `title_key` values on the wire (e.g.
// "notifications:studio.ready") already carry the namespace prefix, so `t(item.title_key)` resolves
// directly without prefixing here.

import { TFunction } from "i18next";
import { Camera, LayoutGrid, Image, FileText, User, Download, Layers, Palette, PenLine, Shirt, Bell, MessageCircle, FolderOpen, SwatchBook, type LucideIcon } from "lucide-react";
import { PATHS } from "router/config/paths";
import { INotificationItem, NotificationTargetKind } from "api/notifications/notifications.type";

export type NotificationTone = "success" | "warning" | "error";

export interface INotificationDisplay {
  title: string;
  body: string;
  tone: NotificationTone;
}

// target.kind -> Lucide icon. Matches the whatsNew.data.ts convention: unknown kind falls back to a
// neutral glyph (Bell) rather than crashing the row.
const TARGET_ICONS: Record<NotificationTargetKind, LucideIcon> = {
  studio_project: Camera,
  artistic_sketch_project: PenLine,
  canvas_project: LayoutGrid,
  range_plan: Shirt,
  post: Image,
  techpack: FileText,
  model: User,
  download: Download,
  fabric: Layers,
  moodboard: Palette,
  moodboard_folder: FolderOpen,
  // The Brand Kits menu entry's own glyph (`icons/menuIcons` MenuBrandKits).
  brand_kit_folder: SwatchBook,
  canvas_comment: MessageCircle,
  photoshoot_draft: Camera,
};

// The failed-row body of the kinds whose only notification is a failure (a folder upload).
const FOLDER_FAILED_BODY: Partial<Record<NotificationTargetKind, string>> = {
  moodboard_folder: "notifications:moodboard_folder.failed",
  brand_kit_folder: "notifications:brand_kit_folder.failed",
};

export const getNotificationIcon = (item: INotificationItem): LucideIcon => TARGET_ICONS[item.target?.kind] || Bell;

// Unit-count body, e.g. "3 products generated". `t("notifications:units.<unit>", { count })`
// resolves the ICU plural form (see notifications.json `units.*`), then that resolved string is
// interpolated into `notifications:body.generated`.
// Only Design Canvas (`canvas_project`) and Photoshoot Studio (`studio_project`) genuinely produce
// multiple results in a single notification, so only they surface the real count. Every other
// generation is conceptually ONE result to the user — a back view's 4 candidate variations are still
// one back view, two technical-drawing versions are still one drawing — so we pin the count to 1 there
// rather than expose the BE's internal batch/candidate count (REF-6306). Unit is kept (→ "1 file" for a
// download, "1 image" for a tech pack), only the number is normalised.
const MULTI_RESULT_KINDS = new Set<NotificationTargetKind>(["canvas_project", "studio_project", "artistic_sketch_project", "range_plan"]);

// …and a unit the BE only ever sends WITH a real count (REF-6509). `variation` exists precisely
// to say "N candidates of ONE thing" — `writer._effective_title_and_unit` picks it over `view` for
// the back-view grid for that reason — so pinning it to 1 threw away the one number it carries and
// rendered "1 variation" for a four-candidate run. Driving this off the unit rather than the kind
// keeps the pin above intact for every family that really does deliver one result.
const REAL_COUNT_UNITS = new Set<INotificationItem["params"]["unit"]>(["variation"]);

const buildCompletedBody = (item: INotificationItem, t: TFunction): string => {
  const { unit } = item.params;
  // An analysis generates nothing yet — it found N products, ready for the shoot.
  if (item.target?.kind === "photoshoot_draft") return t("notifications:body.ready_to_shoot", { unit: t(`notifications:units.${unit}`, { count: item.params.count }) });
  const count = MULTI_RESULT_KINDS.has(item.target?.kind) || REAL_COUNT_UNITS.has(unit) ? item.params.count : 1;
  const unitLabel = t(`notifications:units.${unit}`, { count });

  return t("notifications:body.generated", { unit: unitLabel });
};

// REF-6865 — a brand kit built from files is `partial` only when a cap left items out (the BE's
// `("brand_kit_folder", "capped")` row counts added vs left out): say how many were left out, not "N of M
// done" — nothing failed, the kit is full.
const buildPartialBody = (item: INotificationItem, t: TFunction): string => {
  if (item.target?.kind === "brand_kit_folder") return t("notifications:brand_kit_folder.capped", { count: item.failure_count });

  return t("notifications:status.partial", {
    success: item.success_count,
    total: item.success_count + item.failure_count,
  });
};

// REF-6509: this is the FAILED row's TITLE now, not its body — see `resolveNotificationDisplay`.
// The name is kept because the string it resolves is unchanged and every locale already carries
// it; renaming would have churned nothing but the reader's memory of where it came from.
const buildFailedTitle = (t: TFunction): string => t("notifications:status.failed");

export const toneForStatus = (status: INotificationItem["status"]): NotificationTone => {
  if (status === "failed") return "error";
  if (status === "partial") return "warning";

  return "success";
};

// Resolves the title (always the completed-state copy at `title_key`, regardless of status — the
// title names WHAT finished, the body says HOW it finished) and the status-appropriate body.
export const resolveNotificationDisplay = (item: INotificationItem, t: TFunction): INotificationDisplay => {
  // REF-6509: the title used to be `t(item.title_key)` UNCONDITIONALLY, and every `title_key` in
  // the catalog is a completion sentence — so a failed row read "Modeliniz hazır" above
  // "Üretim başarısız oldu". Both halves of one row, contradicting each other, in every family.
  //
  // A FAILED row takes the status as its title and drops the body: repeating the same sentence
  // underneath adds nothing, and the row already says which feature it belongs to through
  // `getNotificationIcon` and its error tone. No per-feature failure strings are invented for
  // this — there are 40-odd families across seven locales, and none of that vocabulary buys
  // anything the icon does not already carry.
  //
  // A PARTIAL row keeps the completion title on purpose. "Fotoğraf çekiminiz hazır" above
  // "3 işlemden 2 tamamlandı" is not a contradiction — some results genuinely are ready, and
  // that is the one thing the user wants to know before clicking.
  if (item.status === "failed") {
    // REF-6804 / REF-6865 — a folder upload has no page of its own to explain itself, so its failed row
    // says what failed; it opens the Moodboards / Brand Kits list (`getNotificationRoute`).
    const bodyKey = FOLDER_FAILED_BODY[item.target?.kind];
    const body = bodyKey ? t(bodyKey) : "";

    return { title: buildFailedTitle(t), body, tone: toneForStatus(item.status) };
  }

  // REF-6607 — a comment mention is not a generation: the title names WHO mentioned the user and the
  // body is the comment itself (plain text, never a translation key). No count/unit copy.
  if (item.target?.kind === "canvas_comment") {
    const actor = item.params?.actor_name?.trim() || t("notifications:canvas_comment.someone");

    // `canvas_comment.reply` (a thread the user follows) coalesces while unread: `count` replies, latest actor + preview.
    return { title: t(item.title_key, { actor_name: actor, count: item.params?.count ?? 1 }), body: item.params?.preview?.trim() || "", tone: "success" };
  }

  const title = t(item.title_key);
  const body = item.status === "partial" ? buildPartialBody(item, t) : buildCompletedBody(item, t);

  return { title, body, tone: toneForStatus(item.status) };
};

// Route resolver. Uses the same route shapes as components/RecentProjectsGrid/RecentProjectsGrid.tsx
// (routeForProject) for studio/canvas/techpack so a notification opens the same place a Recent
// Projects tile would. `download` has no destination — re-trigger/download is handled by the bell UI
// itself, not by navigation.
export const getNotificationRoute = (item: INotificationItem): string | null => {
  const { target } = item;

  switch (target.kind) {
    case "studio_project": {
      if (!target.project_id) return PATHS.PHOTOSHOOTS_STUDIO;

      const base = PATHS.PHOTOSHOOTS_STUDIO_DETAIL.replace(":id", target.project_id);

      // REF-6306 — only a studio EDIT notification carries both product_id + image_url; creation
      // notifications (studio.ready) have neither, so they stay project-only. Gate on BOTH so an
      // old/partial payload never opens the modal with a missing image.
      if (!target.product_id || !target.image_url) return base;

      return `${base}?focusProduct=${target.product_id}&focusImage=${encodeURIComponent(target.image_url)}&focusEditType=${target.edit_type ?? ""}`;
    }
    case "canvas_project": {
      // `target.design_id` is the design_canvas JOB id, NOT the canvas project id — opening
      // `/design-canvas?id=<jobId>` 404s (that route loads a project via GET canvas-projects/{id}).
      // Route only when BE supplies the real canvas project id as `target.project_id` (REF-6306 BE
      // follow-up); otherwise fall back to the projects list. When a project id IS present, also
      // deep-link to the specific result via ?focusResult=<design_id> — DesignCanvas resolves it to
      // either the top-level node it generated, or the edit thumbnail carrying that design id.
      if (!target.project_id) return PATHS.CANVAS_PROJECTS;
      const base = `${PATHS.DESIGN_STUDIO}?id=${target.project_id}`;

      return target.design_id ? `${base}&focusResult=${target.design_id}` : base;
    }
    case "range_plan":
      // A Collection Range Plan — REF-6509. The plan is the thing the user waited ten to twenty
      // minutes for, and it never announced itself: in creative mode each of its N internal
      // design-canvas children rang the bell instead, deep-linking into a canvas the plan had
      // not built yet (the BE now suppresses those and announces the plan once).
      //
      // The destination forks exactly the way the plan's own card does
      // (`CollectionRangePlansPage`): the canvas when the plan built one, the plans list
      // otherwise — which is also the only page that can show a FAILED plan, so this needs no
      // null branch.
      // `project_id` is the canvas (the same slot `canvas_project` uses for it), `design_id` the
      // plan. Reusing them keeps the BE response model — and the contract snapshot — untouched.
      return target.project_id ? `${PATHS.DESIGN_STUDIO}?id=${target.project_id}` : PATHS.COLLECTION_RANGE_PLANS;
    case "post":
      // `post` is a legacy / mis-filed target kind (REF-6306). The old standalone photoshoot flow it
      // pointed at (`/photoshoot/:id`) is RETIRED — that route isn't even registered, and PhotoshootPage
      // is unrouted. The design_types still tagged `post` split into: (a) live Design-Canvas tools
      // (extract_print, seamless_tile, denim_laser, convert_to_sketch, canvas-invoked edits, design_video)
      // whose id is a canvas JOB id with no project_id; (b) mis-filed Tech Pack / Studio types
      // (get_backview, artistic_sketch, create_video); (c) genuinely dead types (tryon/ghost/mannequin,
      // txt2img/design/pattern, legacy standalone fixes, marketplace) nothing generates anymore.
      // None can be routed FE-side (no valid id/route). BE RE-TARGETS the live ones to their real
      // kind (canvas_project / techpack / studio_project) + ids so they flow through the resolvers
      // above — that half has shipped (`notifications/writer.py::_effective_target_kind`), so the
      // families reaching this branch today are the STANDALONE invocations (a canvas tool run with
      // no canvas, whose result has no screen to open) plus the genuinely dead types. A frame that
      // carries its addressing id no longer lands here at all.
      //
      // The retarget only works if the FRAME carries the id, which is why failures used to be less
      // clickable than their own successes: `que.update_error_status` builds its frame from the
      // posts row alone. It now resolves the tech pack link and carries it (REF-6484 follow-up),
      // so a failed tech pack opens the pack like its success does.
      // See docs/be-handover/REF-6306-post-notification-retarget.md.
      return null;
    case "techpack": {
      // `/tech-pack/:id` loads a project via get_project_detail?techpack_project_id=<id>. But every
      // techpack notification's `target.design_id` is a WRONG id space (a techpack_info row id, a posts
      // id, or a synthetic job id) — never the techpack_project_id — so routing off design_id 404s for
      // all 4 types (same bug class as canvas). Route only when BE supplies the real project id as
      // `target.project_id` (REF-6306 BE follow-up — the generate_view frame already carries it, the
      // others must add it); otherwise fall back to the tech packs list.
      //
      // Deep-link focus (REF-6306): `techpack.ready` has neither view_type nor sketch_url, so it opens
      // the Techpack Document tab (?tab=document). `techpack.ghost_ready` / `techpack.view_ready` carry
      // `view_type` (front|back|side) and open that wizard view tab (?tab=views&view=<view_type>).
      // `techpack.artistic_sketch_ready` additionally carries `sketch_url` — the specific
      // DrawingHistoryStrip entry (identified only by png_url) to select once that view's history has
      // loaded — appended as `&sketchUrl=<encoded png_url>`. All three params are optional and degrade
      // to the pre-existing project-only route when absent.
      if (!target.project_id) return PATHS.TECH_PACKS;

      const base = PATHS.TECH_PACK.replace(":id?", target.project_id);
      if (target.view_type) {
        const viewUrl = `${base}?tab=views&view=${target.view_type}`;

        return target.sketch_url ? `${viewUrl}&sketchUrl=${encodeURIComponent(target.sketch_url)}` : viewUrl;
      }

      return `${base}?tab=document`;
    }
    case "artistic_sketch_project":
      // A bulk sketch RUN, restored on the page it was started from (REF-6509). Its projects are
      // excluded from `/photoshoots/studio` by design, so the studio destination it used to
      // inherit opened a detail page for something the user could find in no list.
      //
      // `?projectId=` and not a dedicated route: `/bulk-process/convert` already polls
      // `GET /artistic-sketch/projects/{id}` for the run it started, so restoring one by id is
      // the same read it was already making — it simply had no way to be TOLD which id.
      return target.project_id ? `${PATHS.BULK_PROCESS_CONVERT}?projectId=${target.project_id}` : PATHS.BULK_PROCESS_CONVERT;
    case "model":
      // `model.ready` (model_draft) → design_id IS the models-table id that /model/:id opens: correct.
      // `model.trained` (train_model) is a legacy/dead flow the FE no longer triggers (its id is a
      // separate legacy table) — it won't fire; if it ever does, it falls through to /model/:id harmlessly.
      return target.design_id ? PATHS.MODEL_DETAIL.replace(":id", target.design_id) : null;
    case "download":
      // 4K / vector-file downloads have no screen — delivery is a browser auto-download when the tab is
      // open. `upscale_image` has no stable re-fetch handle (synthetic job id, no posts row, URL not
      // persisted); re-triggering re-runs + re-charges credits. Non-clickable is the correct interim
      // UNLESS BE supplies `target.download_url` (REF-6306 follow-up) — see getNotificationDownloadUrl.
      return null;
    case "moodboard":
      // Moodboard (trend report) completion — REF-6484. This is the FIRST notification the
      // Moodboards surface has ever produced: the BE pipeline emitted no terminal WS frame at
      // all, so a finished moodboard only ever announced itself by email.
      // `target.design_id` IS the `moodboards` row id, so unlike the canvas/techpack
      // design_id-space bugs above this deep-link is honourable. It opens the board's OWN URL —
      // no longer `/report/:id`, the legacy A4 report page that no menu item and no in-app link
      // outside the orphaned `/trend-analysis` page reaches, which made a notification the only
      // way a user ever landed on it. Falls back to the Moodboards list when the id is missing
      // rather than returning null — the board exists either way, so the user should still land
      // somewhere useful.
      return target.design_id ? PATHS.MOODBOARDS_MY_DETAIL.replace(":id", target.design_id) : PATHS.MOODBOARDS;
    case "moodboard_folder":
      // REF-6804 — a failed folder upload: the notification's own text says why, and it opens the
      // Moodboards list (there is no upload status page — product feedback, 2026-10-10).
      return PATHS.MOODBOARDS;
    case "brand_kit_folder":
      // REF-6865 — a failed brand kit build from files: the row's text says why, and it opens the Brand
      // Kits list. `design_id` is the JOB id, not a kit id — and the kit may have been removed.
      return PATHS.DESIGN_ASSETS_BRAND_KITS;
    case "canvas_comment":
      // REF-6607 — open the canvas at the mentioned thread. Never a bare `/design-canvas` (that CREATES
      // a canvas): no canvas id → the projects list. `focusComment` is consumed by CanvasCommentsProvider.
      if (!target.project_id) return PATHS.CANVAS_PROJECTS;

      return `${PATHS.DESIGN_STUDIO}?id=${encodeURIComponent(target.project_id)}${target.design_id ? `&focusComment=${encodeURIComponent(target.design_id)}` : ""}`;
    case "photoshoot_draft":
      // The analysis's draft reopens the wizard where it left off (usePhotoshootDraft); without an id, the list.
      return target.project_id ? `${PATHS.PHOTOSHOOT_STUDIO}?draft=${encodeURIComponent(target.project_id)}` : PATHS.PHOTOSHOOTS_STUDIO;
    case "fabric":
      // Fabric training completion (REF-6382). `target.design_id` IS the real fabric id (the one
      // GET /v1/fabrics/{id} takes), but the Fabrics grid has no per-card deep-link route today, and
      // BE confirmed dropping to the grid is sufficient — so route there rather than ship a design_id
      // deep-link that can't be honoured (cf. the canvas/techpack design_id-space bugs above). Same
      // destination as the "Fabrics" nav item (`${DESIGN_ASSETS}/fabric`).
      return `${PATHS.DESIGN_ASSETS}/fabric`;
    default:
      return null;
  }
};

// A toast about what the screen OPEN right now already shows says nothing new — the toaster drops it (the bell keeps its
// entry): a photoshoot-analysis toast for the open draft (`?draft=<id>`; photoshoot-analysis-drafts-fe-reply.md §4), and
// a canvas generation on the canvas the design agent's workspace has open (`/design-canvas-agent?id=<canvas>`, kabul A4:
// each of the agent's jobs left a "Your canvas generation is ready" toast that never went away, over the result the
// chat and the canvas were already showing).
export const isToastForOpenDraft = (item: INotificationItem, search: string, pathname?: string): boolean => {
  const open = new URLSearchParams(search);
  if (item.target?.kind === "photoshoot_draft") return !!item.target.project_id && open.get("draft") === item.target.project_id;
  if (item.target?.kind === "canvas_project") return pathname === PATHS.DESIGN_CANVAS_AGENT && !!item.target.project_id && open.get("id") === item.target.project_id;

  return false;
};

// REF-6306 — a stable, re-usable URL for the produced 4K/vector file, present once BE adds it to the
// download-kind target. When present, clicking the notification should download the file (see
// utils/downloadFile.ts) instead of navigating — getNotificationRoute's `download` case stays `null`
// either way, since there is still no *screen* for a download notification.
export const getNotificationDownloadUrl = (item: INotificationItem): string | null => (item.target?.kind === "download" ? item.target.download_url ?? null : null);

/**
 * REF-6607 — the picture a row/toast shows: the BE cover when there is one, else, for a comment
 * mention, the mentioning teammate's photo (the BE deliberately sends no cover for mentions).
 */
export const getNotificationImageUrl = (item: INotificationItem): string | null => item.cover_url || (item.target?.kind === "canvas_comment" ? item.params?.actor_profile_photo || null : null);
